// frontend/src/motion/useHydrated.js
//
// "Has React finished attaching to the prerendered snapshot?"
//
// scripts/prerender.js snapshots the homepage under window.__PRERENDER__, where
// every gate is forced open and every reveal is disarmed - the finished, still
// page crawlers read. A visitor's browser then hydrateRoot()s that HTML
// (src/index.js), and React requires the first client render to produce the
// SAME markup; a component that renders its live, armed state on that first
// pass (a `hl-reveal` class, a count-up at 0, the pinned how-it-works instead
// of the stack) is a hydration mismatch - React 19 then discards the boundary
// and re-renders it from scratch, and logs an error in development.
//
// This is the canonical answer: useSyncExternalStore with a server snapshot of
// `false` and a client snapshot of `true`. During hydration React uses the
// SERVER snapshot, so the first render matches the still page; it then
// re-renders synchronously with the client value, before paint. On a plain
// client-only boot (createRoot, no snapshot - the dev server, a route change)
// the client snapshot is used from the first render and nothing changes.
//
// `subscribe` never emits: the value flips exactly once, by React's own
// hydration bookkeeping, never by an event.

import { useSyncExternalStore } from "react";

const subscribe = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

/**
 * @returns {boolean} false during the hydration render of a prerendered
 *   snapshot, true from the first post-hydration render on - and always true
 *   on a client-only boot.
 */
export default function useHydrated() {
  return useSyncExternalStore(subscribe, getClientSnapshot, getServerSnapshot);
}

export { useHydrated };
